import { useState, useCallback, useRef, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { PageLayout } from './layout'
import CreateSaleModal from './CreateSaleModal'
import PackingModal from './PackingModal'
import WeeklyOrdersModal from './WeeklyOrdersModal'
import WeeklyOrdersPopover from './WeeklyOrdersPopover'
import LastDeliveredPopover from './LastDeliveredPopover'
import HistoricalInvoiceModal from './HistoricalInvoiceModal'
import EditRecurringOrderDialog from './EditRecurringOrderDialog'
import PriceChangeConfirmDialog from './sales-timeline/PriceChangeConfirmDialog'
import AdjustmentConfirmationModal from './sales-timeline/AdjustmentConfirmationModal'
import { CustomerDetailModal } from '@/components/customer'
import { Timeline } from './sales-timeline'
import { useTimelineData } from './sales-timeline/useTimelineData'
import { useDataCache } from '@/contexts/DataCacheContext'
import { fetchCustomer, calculateAdjustmentPreview, applySaleItemAdjustment } from '@/supabase-client'
import { Button } from '@/components/ui/button'
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from '@/components/ui/alert-dialog'
import { Label } from '@/components/ui/label'
import SearchInput from '@/components/ui/search-input'
import { deleteSale } from '../supabase-client'
import { toast } from 'sonner'
import './SalesRecord.css'

export default function SalesRecord({ user, onLogout }) {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isPackingModalOpen, setIsPackingModalOpen] = useState(false)
  const [isManageOrdersOpen, setIsManageOrdersOpen] = useState(false)
  const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false)
  const [isEditRecurringOrderOpen, setIsEditRecurringOrderOpen] = useState(false)
  const [viewingInvoice, setViewingInvoice] = useState(null)
  const [editingSale, setEditingSale] = useState(null)
  const [editingRecurringOrder, setEditingRecurringOrder] = useState(null)
  const [formData, setFormData] = useState(() => {
    try {
      const raw = localStorage.getItem('odin_create_sale_draft')
      return raw ? JSON.parse(raw) : null
    } catch { return null }
  })
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCustomer, setSelectedCustomer] = useState(null)
  const [customerModalOpen, setCustomerModalOpen] = useState(false)
  const [adjustmentPreview, setAdjustmentPreview] = useState(null)
  const [adjustmentModalOpen, setAdjustmentModalOpen] = useState(false)
  const [adjustmentLoading, setAdjustmentLoading] = useState(false)
  const [pendingAdjustmentItem, setPendingAdjustmentItem] = useState(null)
  const [batchAdjustmentConfirmOpen, setBatchAdjustmentConfirmOpen] = useState(false)
  const [batchAdjustmentData, setBatchAdjustmentData] = useState(null)
  const [batchAdjustmentReason, setBatchAdjustmentReason] = useState('')
  const [batchAdjustmentSaving, setBatchAdjustmentSaving] = useState(false)

  const navigate = useNavigate()
  const { productTypes: cachedProductTypes, getProductTypes } = useDataCache()
  const timelineRef = useRef(null)

  // Timeline data hook
  const {
    pastItems,
    loadingPast,
    hasMorePast,
    loadMorePast,
    updateStatus,
    addSaleOptimistically,
    updateSaleOptimistically,
    persistSaleInBackground,
    confirmingIds,
    searchPast,
    refreshFutureItems,
    pendingPriceCheck,
    confirmPriceUpdate,
    cancelPriceUpdate,
    addSaleToTimeline,
    removeSaleFromTimeline,
    updateSaleItemInTimeline,
    initialLoadComplete
  } = useTimelineData()

  // Stabilise confirmingIds array for React.memo on Timeline
  const confirmingIdsArray = useMemo(() => Array.from(confirmingIds), [confirmingIds])

  // Handle search input changes
  const handleSearchChange = useCallback((e) => {
    const value = e.target.value
    setSearchTerm(value)
    searchPast(value)
  }, [searchPast])

  const handleCustomerClick = useCallback(async (customer) => {
    if (!customer?.id) return
    try {
      const fullCustomer = await fetchCustomer(customer.id)
      setSelectedCustomer({ ...fullCustomer, companyName: fullCustomer.name })
      setCustomerModalOpen(true)
    } catch (err) {
      console.error('[SalesRecord] Error fetching customer:', err)
    }
  }, [])

  const handleCreateSale = () => {
    setEditingSale(null)
    setIsModalOpen(true)
  }

  // Handle edit from timeline - opens the sale in the modal for editing
  const handleEditSale = useCallback((item) => {
    // Convert timeline item back to sale format for the modal
    // The sourceId is the actual sale ID
    const saleId = item.sourceId || item.id.replace('sale-', '')
    setEditingSale({
      id: saleId,
      customer_id: item.customer?.id,
      branch_id: item.branch?.id,
      date: item.date, // Must be 'date' to match CreateSaleModal expectations
      status: item.status,
      consignment_no: item.consignmentNo,
      items: item.items?.map(i => ({
        product_id: i.productId,
        product_type_id: i.productTypeId,
        block_format_id: i.blockFormatId || null,
        block_format: i.blockFormat || null,
        growing_material_id: i.growingMaterialId || null,
        growing_material: i.growingMaterial || null,
        productType: i.productTypeName, // Pass name for CreateSaleModal matching
        quantity: i.quantity,
        price: i.price,
        discount_percent: i.discountPercent || 0,
        total: i.subtotal
      })) || []
    })
    setIsModalOpen(true)
  }, [])

  // Handle view invoice from timeline
  const handleViewInvoice = useCallback((item) => {
    // Convert timeline item to invoice format for the modal
    setViewingInvoice({
      invoiceNumber: item.invoiceNo,
      customer: { name: item.customer?.name },
      date: item.date,
      sales: [{
        consignment_no: item.consignmentNo,
        items: item.items?.map(i => ({
          strain: i.strain || i.productTypeName,
          quantity: i.quantity,
          price_per_kg: i.price,
          sub_total: i.subtotal
        })) || [],
        total: item.total
      }]
    })
    setIsInvoiceModalOpen(true)
  }, [])

  const handleCloseInvoiceModal = useCallback(() => {
    setIsInvoiceModalOpen(false)
    setViewingInvoice(null)
  }, [])

  // Handle edit recurring order from timeline
  const handleEditRecurringOrder = useCallback((item) => {
    // The timeline item has recurringOrderId from the instance
    // sourceId is the instance ID for "this order only" saves
    setEditingRecurringOrder({
      id: item.recurringOrderId, // The recurring order ID
      instanceId: item.sourceId, // The instance ID for "this order only" saves
      customer_id: item.customer?.id,
      branch_id: item.branch?.id,
      days_of_week: item.daysOfWeek || [],
      end_type: item.endType || 'indefinite',
      end_date: item.endDate || null,
      max_occurrences: item.maxOccurrences || null,
      items: item.items?.map(i => ({
        product_id: i.productId,
        product_type_id: i.productTypeId,
        quantity: i.quantity
      })) || []
    })
    setIsEditRecurringOrderOpen(true)
  }, [])

  const handleCloseEditRecurringOrder = useCallback(() => {
    setIsEditRecurringOrderOpen(false)
    setEditingRecurringOrder(null)
  }, [])

  const handleSaveRecurringOrder = useCallback(() => {
    // Close dialog and refresh timeline to show changes
    setIsEditRecurringOrderOpen(false)
    setEditingRecurringOrder(null)
    // Refresh future items to reflect the saved changes
    refreshFutureItems()
  }, [refreshFutureItems])

  const handleOpenPackingModal = () => {
    setIsPackingModalOpen(true)
  }

  const handleClosePackingModal = () => {
    setIsPackingModalOpen(false)
  }

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false)
  }, [])

  const handleFormDataChange = useCallback((data) => {
    setFormData(data)
    try {
      if (data) localStorage.setItem('odin_create_sale_draft', JSON.stringify(data))
      else localStorage.removeItem('odin_create_sale_draft')
    } catch { /* localStorage unavailable */ }
  }, [])

  const handleClearFormData = useCallback(() => {
    setFormData(null)
    localStorage.removeItem('odin_create_sale_draft')
  }, [])

  const handleSaveSale = useCallback((payload) => {
    setFormData(null)
    localStorage.removeItem('odin_create_sale_draft')
    setIsModalOpen(false)

    if (!payload) return

    if (payload._isEdit) {
      // Optimistic update for edit
      const previousItem = updateSaleOptimistically(payload._bgPayload.saleId, payload)
      persistSaleInBackground({ ...payload, _previousItem: previousItem })
    } else {
      // Optimistic add for create
      const tempId = addSaleOptimistically(payload)
      persistSaleInBackground({ ...payload, _tempId: tempId })
    }
  }, [addSaleOptimistically, updateSaleOptimistically, persistSaleInBackground])

  const handleDeleteSale = useCallback(async (saleId) => {
    try {
      await deleteSale(saleId)
      removeSaleFromTimeline(saleId)
      toast.success('Sale deleted')
    } catch (error) {
      console.error('[SalesRecord] Error deleting sale:', error)
      toast.error('Failed to delete sale', { description: error.message })
    }
  }, [removeSaleFromTimeline])

  const handleRequestReopenModal = useCallback(() => {
    setIsModalOpen(true)
  }, [])

  // Fetch product types for adjustment class dropdown
  const productTypes = useMemo(() => {
    if (cachedProductTypes) return cachedProductTypes
    getProductTypes()
    return []
  }, [cachedProductTypes, getProductTypes])

  // Adjustment flow: user changes quantity or class on an adjustable row
  const handleAdjustmentChange = useCallback(async ({ saleItemId, sourceId, quantity, productTypeId }) => {
    setPendingAdjustmentItem({ saleItemId, sourceId })
    setAdjustmentLoading(true)
    setAdjustmentModalOpen(true)

    try {
      const preview = await calculateAdjustmentPreview(saleItemId, {
        quantity,
        productTypeId
      })
      setAdjustmentPreview(preview)
    } catch (error) {
      console.error('[SalesRecord] Error calculating adjustment preview:', error)
      toast.error('Failed to calculate adjustment', { description: error.message })
      setAdjustmentModalOpen(false)
      setPendingAdjustmentItem(null)
    } finally {
      setAdjustmentLoading(false)
    }
  }, [])

  const handleConfirmAdjustment = useCallback(async (reason) => {
    if (!adjustmentPreview || !pendingAdjustmentItem) return

    try {
      const adjustParams = {
        reason,
        ...(adjustmentPreview.original.quantity !== adjustmentPreview.adjusted.quantity && {
          quantity: adjustmentPreview.adjusted.quantity
        }),
        ...(adjustmentPreview.original.productTypeId !== adjustmentPreview.adjusted.productTypeId && {
          productTypeId: adjustmentPreview.adjusted.productTypeId
        })
      }
      await applySaleItemAdjustment(adjustmentPreview.saleItemId, adjustParams)
      toast.success('Adjustment applied')

      // Refresh the sale in the timeline
      await addSaleToTimeline(pendingAdjustmentItem.sourceId)
    } catch (error) {
      console.error('[SalesRecord] Error applying adjustment:', error)
      toast.error('Failed to apply adjustment', { description: error.message })
    } finally {
      setAdjustmentModalOpen(false)
      setAdjustmentPreview(null)
      setPendingAdjustmentItem(null)
    }
  }, [adjustmentPreview, pendingAdjustmentItem, addSaleToTimeline])

  const handleCancelAdjustment = useCallback(() => {
    setAdjustmentModalOpen(false)
    setAdjustmentPreview(null)
    setPendingAdjustmentItem(null)
  }, [])

  // Batch adjustment flow: user edited multiple line items on an invoiced/paid row
  const handleAcceptAdjustments = useCallback(async ({ changes, item, estimatedCredit }) => {
    setBatchAdjustmentData({ changes, item, estimatedCredit })
    setBatchAdjustmentReason('')
    setBatchAdjustmentConfirmOpen(true)
  }, [])

  const handleConfirmBatchAdjustment = useCallback(async () => {
    if (!batchAdjustmentData) return
    setBatchAdjustmentSaving(true)
    try {
      const { changes, item } = batchAdjustmentData
      const sourceId = item.sourceId || item.id.replace('sale-', '')

      for (const [saleItemId, change] of Object.entries(changes)) {
        await applySaleItemAdjustment(saleItemId, {
          ...(change.quantity != null && { quantity: change.quantity }),
          ...(change.productTypeId != null && { productTypeId: change.productTypeId }),
          reason: batchAdjustmentReason
        })
      }

      await addSaleToTimeline(sourceId)
      toast.success(`${Object.keys(changes).length} adjustment${Object.keys(changes).length > 1 ? 's' : ''} applied`)
    } catch (error) {
      console.error('[SalesRecord] Error applying batch adjustments:', error)
      toast.error('Failed to apply adjustments', { description: error.message })
    } finally {
      setBatchAdjustmentSaving(false)
      setBatchAdjustmentConfirmOpen(false)
      setBatchAdjustmentData(null)
      setBatchAdjustmentReason('')
    }
  }, [batchAdjustmentData, batchAdjustmentReason, addSaleToTimeline])

  return (
    <PageLayout
      title="Sales Record"
      user={user}
      onLogout={onLogout}
      fillHeight
      showFooter={false}
      headerActions={
        <>
          <WeeklyOrdersPopover
            onOrderConfirmed={addSaleToTimeline}
            onManageOrders={() => setIsManageOrdersOpen(true)}
          />
          <LastDeliveredPopover />
          <Button
            variant="outline"
            onClick={handleOpenPackingModal}
          >
            📦 Packing
          </Button>
          <Button
            variant="create"
            onClick={handleCreateSale}
          >
            + Create Sale
          </Button>
          <SearchInput
            placeholder="Search past records..."
            value={searchTerm}
            onChange={handleSearchChange}
            className="w-48"
          />
        </>
      }
    >
      <div className="flex-1 min-h-0 flex flex-col">
        {/* Timeline Component */}
        <Timeline
          ref={timelineRef}
          pastItems={pastItems}
          onLoadMorePast={loadMorePast}
          loadingPast={loadingPast}
          hasMorePast={hasMorePast}
          onStatusChange={updateStatus}
          onEdit={handleEditSale}
          onEditRecurringOrder={handleEditRecurringOrder}
          onViewInvoice={handleViewInvoice}
          onCustomerClick={handleCustomerClick}
          onAdjustmentChange={handleAdjustmentChange}
          onAcceptAdjustments={handleAcceptAdjustments}
          productTypes={productTypes}
          confirmingIds={confirmingIdsArray}
          initialLoadComplete={initialLoadComplete}
          className="flex-1 min-h-0"
        />
      </div>

      {/* Create/Edit Sale Modal */}
      <CreateSaleModal
        key={editingSale?.id || 'new'}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveSale}
        onDelete={handleDeleteSale}
        onRequestReopen={handleRequestReopenModal}
        editingSale={editingSale}
        savedFormData={formData}
        onFormDataChange={handleFormDataChange}
        onClearFormData={handleClearFormData}
      />

      {/* Packing Modal */}
      <PackingModal
        isOpen={isPackingModalOpen}
        onClose={handleClosePackingModal}
        onRefresh={refreshFutureItems}
        updateStatus={updateStatus}
        onItemQuantityUpdate={updateSaleItemInTimeline}
      />

      {/* Weekly Orders Modal (full management) */}
      <WeeklyOrdersModal
        isOpen={isManageOrdersOpen}
        onClose={() => setIsManageOrdersOpen(false)}
      />

      {/* Invoice Viewing Modal */}
      {isInvoiceModalOpen && viewingInvoice && (
        <HistoricalInvoiceModal
          invoice={viewingInvoice}
          onClose={handleCloseInvoiceModal}
        />
      )}

      {/* Edit Recurring Order Dialog */}
      {editingRecurringOrder && (
        <EditRecurringOrderDialog
          order={editingRecurringOrder}
          instanceId={editingRecurringOrder.instanceId}
          customerId={editingRecurringOrder.customer_id}
          open={isEditRecurringOrderOpen}
          onOpenChange={handleCloseEditRecurringOrder}
          onSave={handleSaveRecurringOrder}
        />
      )}

      {/* Price Change Confirmation Dialog (pending→packed) */}
      <PriceChangeConfirmDialog
        pendingPriceCheck={pendingPriceCheck}
        onConfirm={confirmPriceUpdate}
        onCancel={cancelPriceUpdate}
      />

      {/* Adjustment Confirmation Modal */}
      <AdjustmentConfirmationModal
        open={adjustmentModalOpen}
        preview={adjustmentPreview}
        loading={adjustmentLoading}
        onConfirm={handleConfirmAdjustment}
        onCancel={handleCancelAdjustment}
      />

      {/* Batch Adjustment Confirmation Dialog (invoiced/paid sales) */}
      <AlertDialog open={batchAdjustmentConfirmOpen} onOpenChange={(open) => {
        if (!open) {
          setBatchAdjustmentConfirmOpen(false)
          setBatchAdjustmentData(null)
          setBatchAdjustmentReason('')
        }
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure you'd like to proceed?</AlertDialogTitle>
            <AlertDialogDescription>
              {batchAdjustmentData && (() => {
                const { item, estimatedCredit } = batchAdjustmentData
                const customerName = item.customer?.name || 'Unknown'
                const branchName = item.branch?.name && item.branch.name.toLowerCase() !== 'main'
                  ? item.branch.name
                  : null
                const label = branchName ? `${customerName}-${branchName}` : customerName
                const amount = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(Math.abs(estimatedCredit))
                return `Clicking yes will credit ${label}-${amount}`
              })()}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="mt-2">
            <Label htmlFor="adjustment-reason">Reason <span className="text-red-500">*</span></Label>
            <textarea
              id="adjustment-reason"
              value={batchAdjustmentReason}
              onChange={(e) => setBatchAdjustmentReason(e.target.value)}
              placeholder="e.g. Customer returned unsold stock"
              rows={2}
              className="mt-1 w-full px-3 py-2 text-sm rounded-md border border-input bg-background"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setBatchAdjustmentConfirmOpen(false)
              setBatchAdjustmentData(null)
              setBatchAdjustmentReason('')
            }}>No</AlertDialogCancel>
            <AlertDialogAction
              disabled={!batchAdjustmentReason.trim() || batchAdjustmentSaving}
              onClick={handleConfirmBatchAdjustment}
            >
              {batchAdjustmentSaving ? 'Saving...' : 'Yes'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Customer Detail Modal */}
      {selectedCustomer && (
        <CustomerDetailModal
          customer={selectedCustomer}
          open={customerModalOpen}
          onOpenChange={(open) => {
            setCustomerModalOpen(open)
            if (!open) setSelectedCustomer(null)
          }}
        />
      )}
    </PageLayout>
  )
}
